import { api, INVALIDATION_TAGS } from 'reduxStore/api';
import {
  ADD_AFFILIATE_NOTE,
  CALCULATE_NOW,
  CHANGE_ONE_TIME_PASSWORD,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_ACCOUNT,
  GET_ACTIVITY_STATISTIC_BY_USERS_LIST,
  GET_AFFILIATES_LIST,
  GET_AFFILIATES_SHORT_INFO,
  LAST_CALCULATE_DATE,
  REGISTRATION,
  REMOVE_AFFILIATE,
  SET_AFFILIATES_STATUS_BULK,
  UPDATE_AFFILIATE_ROLE,
  UPLOAD_STATUS_CHANGE_LIST_BY_FILE,
  GET_UPLOADED_STATUS_CHANGE_DATA_LIST,
  SET_AFFILIATE_STATUS_BY_FILE,
  PHONE_NUMBER_VERIFICATION_CODE,
  UPDATE_AVAILABLE_WALLET,
  GET_PARTNER_BONUS_COST,
  SET_CONVERTED_BONUS_COST,
  RESET_TWO_FACTOR_AUTH_SECRET,
  GET_VERIFICATION_DOC,
  SAVE_VERIFICATION_DOCUMENT,
  CHANGE_EMAIL,
  CHANGE_STATUS_ADMIN_FOR_SEND_EMAIL,
  UPDATE_CURRENT_USER_INFO,
  UPDATE_USER_INFO,
  UPLOAD_CORRECTION_LIST_BY_FILE,
  GET_UPLOADED_CORRECTION_LIST,
  UPDATE_AVAILABLE_WALLETS_BY_FILE,
  GET_TRANSFERRED_AFFILIATES,
} from 'constants/api';
import { changeUserInfo } from 'reduxStore/slices/userSlice';
import { PERMISSION_STATUS } from 'constants/defines';

const updateUserInfo = (dispatch, params) => {
  dispatch(
    changeUserInfo({ ...params, ...(params.smsVerificationCode && { verifiedSms: PERMISSION_STATUS.ACCEPTED }) })
  );

  dispatch(api.util.invalidateTags([INVALIDATION_TAGS.GET_AFFILIATE_PERSONAL_INFO_LOG]));
};

export const userApi = api.injectEndpoints({
  endpoints: builder => ({
    createUser: builder.mutation({
      query: credentials => ({
        url: REGISTRATION,
        method: 'POST',
        body: credentials,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        if (data?.result) {
          dispatch(userApi.util.invalidateTags([INVALIDATION_TAGS.GET_USERS]));
        }
      },
    }),
    getUsers: builder.query({
      query: credentials => ({
        url: GET_AFFILIATES_LIST,
        method: 'POST',
        body: credentials,
      }),
      providesTags: [INVALIDATION_TAGS.GET_USERS],
    }),
    removeUser: builder.mutation({
      query: credentials => ({
        url: REMOVE_AFFILIATE,
        method: 'POST',
        body: credentials,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        if (data?.result) {
          dispatch(userApi.util.invalidateTags([INVALIDATION_TAGS.GET_USERS]));
        }
      },
    }),
    addUserNote: builder.mutation({
      query: credentials => ({
        url: ADD_AFFILIATE_NOTE,
        method: 'POST',
        body: credentials,
      }),
      onQueryStarted: async (credentials, { dispatch, getState, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        const { filterParams } = getState().filter;
        const { affiliateId, note } = credentials;
        if (data?.result) {
          dispatch(
            userApi.util.updateQueryData('getUsers', filterParams, draft => {
              draft.result.records = draft.result.records.map(item =>
                item.affiliateId === affiliateId ? { ...item, note } : item
              );
            })
          );
        }
      },
    }),
    getPartnerLastCalculationDate: builder.query({
      query: () => ({
        url: LAST_CALCULATE_DATE,
        method: 'POST',
      }),
    }),
    calculateNow: builder.mutation({
      query: credentials => ({
        url: CALCULATE_NOW,
        method: 'POST',
        body: credentials,
      }),
    }),
    setUserStatusBulk: builder.mutation({
      query: ({ statusId, affiliateIds }) => ({
        url: SET_AFFILIATES_STATUS_BULK,
        method: 'POST',
        body: {
          affiliateList: affiliateIds,
          statusId,
        },
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const { statusId, isAccount } = body;

        if (data?.result) {
          dispatch(
            api.util.invalidateTags([
              INVALIDATION_TAGS.GET_USERS,
              INVALIDATION_TAGS.GET_COMMISSION_PLAN_LOG,
              INVALIDATION_TAGS.GET_AFFILIATE_CHANGE_STATUS_LOGS,
            ])
          );

          isAccount && dispatch(changeUserInfo({ statusId }));
        }
      },
    }),
    updateUserRole: builder.mutation({
      query: body => ({
        url: UPDATE_AFFILIATE_ROLE,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const { roleId } = body;

        if (data?.result) {
          dispatch(api.util.invalidateTags([INVALIDATION_TAGS.GET_USERS, INVALIDATION_TAGS.GET_COMMISSION_PLAN_LOG]));
          dispatch(changeUserInfo({ roleId }));
        }
      },
    }),
    getAffiliateShortInfo: builder.query({
      query: body => ({
        url: GET_AFFILIATES_SHORT_INFO,
        method: 'POST',
        body,
      }),
      transformResponse: response => {
        const { result } = response;
        if (!Array.isArray(result)) {
          const {
            totals: { total },
          } = result;

          return total;
        }
      },
    }),
    getActivityByUsers: builder.query({
      query: body => ({
        url: GET_ACTIVITY_STATISTIC_BY_USERS_LIST,
        method: 'POST',
        body,
      }),
    }),
    passwordChange: builder.mutation({
      query: body => {
        const apiEndpoint = body.type
          ? body.isAgent
            ? CHANGE_PASSWORD
            : CHANGE_PASSWORD_ACCOUNT
          : CHANGE_ONE_TIME_PASSWORD;

        return {
          url: apiEndpoint,
          method: 'POST',
          body: body.password,
        };
      },
    }),
    changeStatusByFile: builder.mutation({
      query: body => ({
        url: UPLOAD_STATUS_CHANGE_LIST_BY_FILE,
        method: 'POST',
        body,
      }),
    }),
    getStatusChangeList: builder.query({
      query: body => ({
        url: GET_UPLOADED_STATUS_CHANGE_DATA_LIST,
        method: 'POST',
        body,
      }),
      transformResponse: response => response.result,
    }),
    setAffiliateStatusByFile: builder.mutation({
      query: statusId => ({
        url: SET_AFFILIATE_STATUS_BY_FILE,
        method: 'POST',
        body: { statusId },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data?.result) {
          dispatch(
            api.util.invalidateTags([INVALIDATION_TAGS.GET_USERS, INVALIDATION_TAGS.GET_AFFILIATE_CHANGE_STATUS_LOGS])
          );
        }
      },
    }),
    phoneNumberVerificationCode: builder.query({
      query: body => ({
        url: PHONE_NUMBER_VERIFICATION_CODE,
        method: 'POST',
        body,
      }),
    }),
    updateAvailableWallet: builder.mutation({
      query: body => ({
        url: UPDATE_AVAILABLE_WALLET,
        method: 'POST',
        body,
      }),
    }),
    getPartnerBonusCost: builder.query({
      query: body => ({
        url: GET_PARTNER_BONUS_COST,
        method: 'POST',
        body,
      }),
      providesTags: [INVALIDATION_TAGS.GET_PARTNER_BONUS_COST],
    }),
    setPartnerBonusCost: builder.mutation({
      query: body => ({
        url: SET_CONVERTED_BONUS_COST,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const { affiliateId } = body;

        if (data?.result) {
          dispatch(
            userApi.util.updateQueryData('getPartnerBonusCost', { affiliateId }, draft => {
              draft.result.records[0].percent = parseFloat(body.percent).toFixed(2);
            })
          );
        }
      },
    }),
    resetTwoFactorAuth: builder.mutation({
      query: body => ({
        url: RESET_TWO_FACTOR_AUTH_SECRET,
        method: 'POST',
        body,
      }),
    }),
    getVerificationDoc: builder.query({
      query: body => ({
        url: GET_VERIFICATION_DOC,
        method: 'POST',
        body,
      }),
      providesTags: [INVALIDATION_TAGS.GET_VERIFICATION_DOC],
    }),
    saveVerificationDoc: builder.mutation({
      query: body => ({
        url: SAVE_VERIFICATION_DOCUMENT,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        if (data?.result) {
          dispatch(changeUserInfo({ verificationDocument: [body.verificationDocument] }));
          dispatch(api.util.invalidateTags([INVALIDATION_TAGS.GET_VERIFICATION_DOC]));
        }
      },
    }),
    changeEmail: builder.mutation({
      query: body => ({
        url: CHANGE_EMAIL,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        const { email } = body;

        if (data?.result) {
          dispatch(changeUserInfo({ email }));
        }
      },
    }),
    updateUserAutoMessaging: builder.mutation({
      query: body => ({
        url: CHANGE_STATUS_ADMIN_FOR_SEND_EMAIL,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const { isSend } = body;
        if (data?.result) {
          dispatch(changeUserInfo({ isSend }));
        }
      },
    }),
    updateCurrentUserInfo: builder.mutation({
      query: body => ({
        url: UPDATE_CURRENT_USER_INFO,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const { affiliate } = body;

        if (data?.result) {
          updateUserInfo(dispatch, affiliate);
        }
      },
    }),
    updateUserInfo: builder.mutation({
      query: body => ({
        url: UPDATE_USER_INFO,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const { affiliate } = body;

        if (data?.result) {
          updateUserInfo(dispatch, affiliate);
        }
      },
    }),
    correctionByFile: builder.mutation({
      query: body => ({
        url: UPLOAD_CORRECTION_LIST_BY_FILE,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        if (data?.result) {
          dispatch(api.util.invalidateTags([INVALIDATION_TAGS.GET_UPLOADED_CORRECTION_LIST]));
        }
      },
    }),
    getCorrectionByFileUsersList: builder.query({
      query: body => ({
        url: GET_UPLOADED_CORRECTION_LIST,
        method: 'POST',
        body,
      }),
      providesTags: [INVALIDATION_TAGS.GET_UPLOADED_CORRECTION_LIST],
    }),
    updateAvailableWalletByFile: builder.mutation({
      query: body => ({
        url: UPDATE_AVAILABLE_WALLETS_BY_FILE,
        method: 'POST',
        body,
      }),
    }),
    getTransferredAffiliates: builder.query({
      query: body => ({
        url: GET_TRANSFERRED_AFFILIATES,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useCreateUserMutation,
  useCalculateNowMutation,
  useUpdateUserRoleMutation,
  useAddUserNoteMutation,
  useRemoveUserMutation,
  usePasswordChangeMutation,
  useLazyGetUsersQuery,
  useGetActivityByUsersQuery,
  useGetAffiliateShortInfoQuery,
  useChangeStatusByFileMutation,
  useLazyGetStatusChangeListQuery,
  useSetUserStatusBulkMutation,
  useLazyGetPartnerLastCalculationDateQuery,
  useSetAffiliateStatusByFileMutation,
  useLazyPhoneNumberVerificationCodeQuery,
  useUpdateAvailableWalletMutation,
  useGetPartnerBonusCostQuery,
  useSetPartnerBonusCostMutation,
  useResetTwoFactorAuthMutation,
  useGetVerificationDocQuery,
  useSaveVerificationDocMutation,
  useChangeEmailMutation,
  useUpdateUserAutoMessagingMutation,
  useUpdateCurrentUserInfoMutation,
  useUpdateUserInfoMutation,
  useCorrectionByFileMutation,
  useGetCorrectionByFileUsersListQuery,
  useUpdateAvailableWalletByFileMutation,
  useLazyGetTransferredAffiliatesQuery,
} = userApi;
